import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ShieldAlert } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('AzuraOS Uncaught Application Error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    // Attempt cache bust reload
    if (typeof window !== 'undefined') {
      // Clear service worker caches if possible
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }
      window.location.reload();
    }
  };

  private handleResetToHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#080A0B] text-white flex items-center justify-center p-4 font-sans">
          <div className="max-w-lg w-full bg-[#121618] border border-rose-500/40 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6 text-center animate-fade-in relative overflow-hidden">
            {/* Ambient subtle glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-400 flex items-center justify-center shadow-lg">
              <AlertTriangle size={28} />
            </div>

            <div className="space-y-2">
              <span className="text-[10px] font-mono font-bold text-rose-400 uppercase tracking-widest bg-rose-500/10 border border-rose-500/20 px-2.5 py-1 rounded-full">
                Sovereign Session Protection Active
              </span>
              <h2 className="text-xl sm:text-2xl font-bold font-display text-white tracking-tight">
                AzuraOS Workspace Self-Healed
              </h2>
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed max-w-md mx-auto">
                Your local financial data in the private vault is completely safe. A temporary display or asset loading issue occurred.
              </p>
            </div>

            {this.state.error && (
              <div className="bg-[#0A0C0D] p-3 rounded-xl border border-[#2A2F31] text-left text-[11px] font-mono text-gray-400 max-h-32 overflow-y-auto">
                <span className="text-rose-400 font-bold block mb-1">Notice:</span>
                {this.state.error.message || 'Unknown runtime exception'}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={this.handleReload}
                className="w-full sm:w-auto px-5 py-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-lg shadow-[#14B8A6]/20"
              >
                <RefreshCw size={14} />
                <span>Refresh &amp; Reconnect OS</span>
              </button>

              <button
                type="button"
                onClick={this.handleResetToHome}
                className="w-full sm:w-auto px-4 py-3 bg-[#1A1F21] hover:bg-[#252C2F] text-gray-200 border border-[#2A2F31] font-semibold rounded-xl text-xs flex items-center justify-center gap-2 transition-all cursor-pointer"
              >
                <Home size={14} />
                <span>Return to Home</span>
              </button>
            </div>

            <p className="text-[10px] text-gray-500 font-mono">
              AzuraOS Sovereign Engine • Encrypted Local Vault Intact
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
